import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { Accordion, Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { ADMIN_SET_USER_SPEND_TIER } from '../../../graphql';

/**
 * States the QA cheat can put a user in. The matcher reads two independent things - the payer flag
 * on AppUser and the spend tier row - so each option sets both to a coherent combination:
 * NPAU clears the payer flag, a tier makes the user a payer in that tier, and PAYER_NO_TIER is the
 * fail-closed case (payer whose tier has not synced) where tier-gated events must stay hidden.
 * Labels and USD ranges match the sale event matcher and the vanity spend breakdown.
 */
const SPEND_TIER_CHEAT_OPTIONS = [
  { value: 'NPAU', label: 'NPAU - never paid' },
  { value: 'MINNOW', label: 'Minnow - USD 0 - 20' },
  { value: 'DOLPHIN', label: 'Dolphin - USD 20 - 100' },
  { value: 'WHALE', label: 'Whale - USD 100 - 1,000' },
  { value: 'KRAKEN', label: 'Kraken - USD > 1,000' },
  { value: 'PAYER_NO_TIER', label: 'Payer without a synced tier (fail-closed case)' },
] as const;

/**
 * The mutation behind this panel is TEST-only on the backend (beta runs against the production
 * database), so the panel itself is hidden anywhere else rather than offering a button that can
 * only fail. `REACT_APP_NAMESPACE` is how the rest of the admin identifies its backend (see
 * src/config.ts); it is undefined for a local dev build, which talks to a local backend.
 */
function canUseSpendTierCheat(): boolean {
  return process.env.NODE_ENV === 'development' || process.env.REACT_APP_NAMESPACE === 'test';
}

type SpendTierResult = {
  userId: string;
  tier?: string | null;
  isPayer: boolean;
  totalRevenue?: number | null;
};

const SpendTierDisplay = ({ userId }: { userId: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<string>('WHALE');
  const [setUserSpendTier, { data, loading, error }] = useMutation<{
    adminSetUserSpendTier: SpendTierResult;
  }>(ADMIN_SET_USER_SPEND_TIER, { notifyOnNetworkStatusChange: true });

  const applyClicked = () => {
    setUserSpendTier({
      variables: {
        // PAYER_NO_TIER is expressed by leaving the tier out: payer, no tier row.
        input: { userId, tier: selected === 'PAYER_NO_TIER' ? null : selected },
      },
    }).catch(() => {
      // surfaced through `error` below
    });
  };

  const result = data?.adminSetUserSpendTier;

  if (!canUseSpendTierCheat()) {
    return null;
  }

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Spend Tier (QA cheat, TEST only)
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              <Row className="mb-2">
                <Col>
                  <small className="text-muted">
                    Forces this user&apos;s lifetime spend state so the sale event spend-tier matcher can be checked
                    without waiting for the daily sync. Only works on the TEST environment. The next daily sync
                    overwrites a forced tier; a cleared payer flag only comes back with a real purchase.
                  </small>
                </Col>
              </Row>
              <Row className="mb-2">
                <Col md={6}>
                  <Form.Control as="select" value={selected} onChange={(e) => setSelected(e.target.value)}>
                    {SPEND_TIER_CHEAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md="auto">
                  <Button onClick={applyClicked} disabled={loading}>
                    {loading ? <Spinner animation="border" size="sm" /> : 'Apply'}
                  </Button>
                </Col>
              </Row>
              {error && <Alert variant="danger">{error.message}</Alert>}
              {result && (
                <Alert variant="success">
                  Now {result.tier ?? 'payer without a synced tier'}
                  {result.isPayer ? '' : ' (non-payer)'}
                  {result.totalRevenue !== null && result.totalRevenue !== undefined
                    ? ` - totalRevenue USD ${result.totalRevenue}`
                    : ''}
                </Alert>
              )}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
};

export default SpendTierDisplay;
